import React, { useState, useEffect, useCallback } from 'react';
import { useRecruitment } from '../../context/RecruitmentContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import {
  RefreshCw, ChevronLeft, ChevronRight, User, Briefcase, UserCheck,
  Calendar as CalendarIcon, MessageSquare, ExternalLink, Loader2
} from 'lucide-react';
import { format, subDays, startOfWeek, startOfMonth } from 'date-fns';

const TIME_PERIODS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'current_month', label: 'Current Month' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'custom', label: 'Custom Time Period' },
];

function getDateRange(period) {
  const now = new Date();
  const fmt = (d) => format(d, 'yyyy-MM-dd');
  switch (period) {
    case 'today': return { from: fmt(now), to: fmt(now) };
    case 'yesterday': { const y = subDays(now, 1); return { from: fmt(y), to: fmt(y) }; }
    case 'tomorrow': { const t = new Date(now); t.setDate(t.getDate() + 1); return { from: fmt(t), to: fmt(t) }; }
    case 'this_week': return { from: fmt(startOfWeek(now, { weekStartsOn: 1 })), to: fmt(now) };
    case 'last_7_days': return { from: fmt(subDays(now, 6)), to: fmt(now) };
    case 'current_month': return { from: fmt(startOfMonth(now)), to: fmt(now) };
    case 'last_30_days': return { from: fmt(subDays(now, 29)), to: fmt(now) };
    default: return { from: fmt(now), to: fmt(now) };
  }
}

const ACTION_COLORS = {
  'CREATE_JOB': 'bg-green-100 text-green-700',
  'UPDATE_JOB': 'bg-blue-100 text-blue-700',
  'DELETE_JOB': 'bg-red-100 text-red-700',
  'CREATE_CANDIDATE': 'bg-green-100 text-green-700',
  'UPDATE_CANDIDATE': 'bg-blue-100 text-blue-700',
  'DELETE_CANDIDATE': 'bg-red-100 text-red-700',
  'UPDATE_STAGE': 'bg-purple-100 text-purple-700',
  'CREATE_TEMPLATE': 'bg-green-100 text-green-700',
  'UPDATE_TEMPLATE': 'bg-blue-100 text-blue-700',
  'DELETE_TEMPLATE': 'bg-red-100 text-red-700',
  'CREATE_INTERVIEW': 'bg-amber-100 text-amber-700',
  'UPDATE_INTERVIEW': 'bg-blue-100 text-blue-700',
  'ADD_FEEDBACK': 'bg-teal-100 text-teal-700',
  'UPDATE_FEEDBACK': 'bg-teal-100 text-teal-700',
  'CREATE_OFFER': 'bg-emerald-100 text-emerald-700',
  'UPDATE_OFFER': 'bg-blue-100 text-blue-700',
  'CONVERT_TO_EMPLOYEE': 'bg-teal-100 text-teal-700'
};

const ENTITY_ICONS = {
  'job': Briefcase,
  'candidate': User,
  'interview': CalendarIcon,
  'pipeline_template': UserCheck,
  'offer': UserCheck
};

const AuditLogs = () => {
  const { getAuditLogs, fetchInterviews } = useRecruitment();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, page_size: 50, total: 0, total_pages: 0 });
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [timePeriod, setTimePeriod] = useState('today');
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);

  // Feedback popup
  const [feedbackPopup, setFeedbackPopup] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    let dateRange;
    if (timePeriod === 'custom' && customFrom && customTo) {
      dateRange = { from: format(customFrom, 'yyyy-MM-dd'), to: format(customTo, 'yyyy-MM-dd') };
    } else if (timePeriod !== 'custom') {
      dateRange = getDateRange(timePeriod);
    } else {
      dateRange = getDateRange('today');
    }
    const result = await getAuditLogs(
      pagination.page,
      pagination.page_size,
      entityFilter === 'all' ? null : entityFilter,
      actionFilter === 'all' ? null : actionFilter,
      dateRange.from,
      dateRange.to
    );
    setLogs(result.data || []);
    setPagination(prev => ({ ...prev, ...result.pagination }));
    setLoading(false);
  }, [pagination.page, entityFilter, actionFilter, timePeriod, customFrom, customTo, getAuditLogs]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    try {
      return format(new Date(timestamp), 'dd MMM yyyy, hh:mm a');
    } catch {
      return timestamp;
    }
  };

  const openFeedback = async (log) => {
    setFeedbackLoading(true);
    setFeedbackPopup({ loading: true, details: log.details, metadata: log.metadata });
    // Fetch the interview to get the feedback text
    const interviewId = log.entity_id;
    try {
      const interviews = await fetchInterviews({});
      const interview = interviews?.find(iv => iv.id === interviewId);
      setFeedbackPopup({
        loading: false,
        details: log.details,
        metadata: log.metadata,
        feedback: interview?.feedback || 'Feedback not available',
        recommendation: interview?.recommendation || log.metadata?.recommendation
      });
    } catch {
      setFeedbackPopup({
        loading: false,
        details: log.details,
        metadata: log.metadata,
        feedback: 'Failed to load feedback',
        recommendation: log.metadata?.recommendation
      });
    }
    setFeedbackLoading(false);
  };

  const renderEntityDetails = (log) => {
    const meta = log.metadata;
    if (!meta) return null;

    const entity = log.entity;
    const action = log.action;

    if (entity === 'candidate') {
      return (
        <div className="mt-1 space-y-0.5 text-xs text-slate-500">
          {meta.candidate_name && (
            <div className="flex items-center gap-1">
              <User className="w-3 h-3" />
              <span className="font-medium text-slate-600">{meta.candidate_name}</span>
            </div>
          )}
          {meta.job_title && (
            <div className="flex items-center gap-1">
              <Briefcase className="w-3 h-3" />
              <span>Job: {meta.job_title}</span>
            </div>
          )}
          {meta.changed_fields && meta.changed_fields.length > 0 && (
            <div className="mt-1 bg-slate-50 rounded px-2 py-1">
              {meta.changed_fields.map((f, i) => (
                <div key={i} className="text-xs">{f}</div>
              ))}
            </div>
          )}
          {action === 'UPDATE_STAGE' && meta.old_stage && (
            <div className="mt-1">
              <Badge variant="outline" className="text-[10px] mr-1">{meta.old_stage}</Badge>
              <span className="text-[10px]">→</span>
              <Badge className="bg-purple-100 text-purple-700 text-[10px] ml-1">{meta.new_stage}</Badge>
            </div>
          )}
          {meta.location && action === 'CREATE_CANDIDATE' && (
            <div>Location: {meta.location}</div>
          )}
          {meta.source && action === 'CREATE_CANDIDATE' && (
            <div>Source: {meta.source}</div>
          )}
        </div>
      );
    }

    if (entity === 'job') {
      return (
        <div className="mt-1 space-y-0.5 text-xs text-slate-500">
          {meta.job_title && (
            <div className="flex items-center gap-1">
              <Briefcase className="w-3 h-3" />
              <span className="font-medium text-slate-600">{meta.job_title}</span>
            </div>
          )}
          {meta.department && <div>Department: {meta.department}</div>}
          {meta.location && <div>Location: {meta.location}</div>}
        </div>
      );
    }

    if (entity === 'interview') {
      return (
        <div className="mt-1 space-y-0.5 text-xs text-slate-500">
          {meta.candidate_name && (
            <div className="flex items-center gap-1">
              <User className="w-3 h-3" />
              <span>Candidate: <span className="font-medium text-slate-600">{meta.candidate_name}</span></span>
            </div>
          )}
          {meta.job_title && (
            <div className="flex items-center gap-1">
              <Briefcase className="w-3 h-3" />
              <span>Job: {meta.job_title}</span>
            </div>
          )}
          {meta.round_name && <div>Round: {meta.round_name}</div>}
          {(action === 'ADD_FEEDBACK' || action === 'UPDATE_FEEDBACK') && (
            <button
              className="text-teal-600 hover:underline font-medium flex items-center gap-1 mt-1"
              onClick={() => openFeedback(log)}
              data-testid={`view-feedback-${log.id}`}
            >
              <MessageSquare className="w-3 h-3" />
              View Feedback
            </button>
          )}
          {meta.recommendation && (
            <div className="mt-1">
              Recommendation: <Badge variant={meta.recommendation?.includes('Hire') ? 'default' : 'destructive'} className="text-[10px]">{meta.recommendation}</Badge>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const periodLabel = TIME_PERIODS.find(t => t.value === timePeriod)?.label || 'Today';

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Audit Logs</h2>
          <p className="text-slate-500">Track all recruitment activities</p>
        </div>
        <Button variant="outline" onClick={loadLogs} disabled={loading} data-testid="refresh-audit-logs">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            {/* Time Period */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Time Period</Label>
              <Select value={timePeriod} onValueChange={(v) => { setTimePeriod(v); setPagination(p => ({...p, page: 1})); }}>
                <SelectTrigger className="w-[200px]" data-testid="audit-time-period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_PERIODS.map(tp => (
                    <SelectItem key={tp.value} value={tp.value}>{tp.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {timePeriod === 'custom' && (
              <>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">From</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[160px] justify-start text-left font-normal" data-testid="audit-custom-from">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {customFrom ? format(customFrom, 'dd MMM yyyy') : 'From date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={customFrom} onSelect={setCustomFrom} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">To</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[160px] justify-start text-left font-normal" data-testid="audit-custom-to">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {customTo ? format(customTo, 'dd MMM yyyy') : 'To date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={customTo} onSelect={setCustomTo} />
                    </PopoverContent>
                  </Popover>
                </div>
                <Button
                  size="sm"
                  onClick={() => { setPagination(p => ({...p, page: 1})); loadLogs(); }}
                  disabled={!customFrom || !customTo}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  Apply
                </Button>
              </>
            )}

            {/* Entity Filter */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Entity</Label>
              <Select value={entityFilter} onValueChange={(v) => { setEntityFilter(v); setPagination(p => ({...p, page: 1})); }}>
                <SelectTrigger className="w-[180px]" data-testid="filter-entity">
                  <SelectValue placeholder="All Entities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  <SelectItem value="job">Jobs</SelectItem>
                  <SelectItem value="candidate">Candidates</SelectItem>
                  <SelectItem value="pipeline_template">Pipeline Templates</SelectItem>
                  <SelectItem value="interview">Interviews</SelectItem>
                  <SelectItem value="offer">Offers</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Action Filter */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Action</Label>
              <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPagination(p => ({...p, page: 1})); }}>
                <SelectTrigger className="w-[200px]" data-testid="filter-action">
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="CREATE_JOB">Create Job</SelectItem>
                  <SelectItem value="UPDATE_JOB">Update Job</SelectItem>
                  <SelectItem value="DELETE_JOB">Delete Job</SelectItem>
                  <SelectItem value="CREATE_CANDIDATE">Create Candidate</SelectItem>
                  <SelectItem value="UPDATE_CANDIDATE">Update Candidate</SelectItem>
                  <SelectItem value="DELETE_CANDIDATE">Delete Candidate</SelectItem>
                  <SelectItem value="UPDATE_STAGE">Update Stage</SelectItem>
                  <SelectItem value="CREATE_INTERVIEW">Create Interview</SelectItem>
                  <SelectItem value="UPDATE_INTERVIEW">Update Interview</SelectItem>
                  <SelectItem value="ADD_FEEDBACK">Add Feedback</SelectItem>
                  <SelectItem value="UPDATE_FEEDBACK">Update Feedback</SelectItem>
                  <SelectItem value="CREATE_OFFER">Create Offer</SelectItem>
                  <SelectItem value="CONVERT_TO_EMPLOYEE">Convert to Employee</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Activity Log
              <Badge variant="outline" className="ml-2 font-normal text-xs">{periodLabel}</Badge>
            </CardTitle>
            <span className="text-sm text-muted-foreground">{pagination.total} entries</span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[180px]">Timestamp</TableHead>
                <TableHead className="w-[120px]">User</TableHead>
                <TableHead className="w-[160px]">Action</TableHead>
                <TableHead className="w-[100px]">Entity</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-purple-600" />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No audit logs found for this period
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log, idx) => {
                  const EntityIcon = ENTITY_ICONS[log.entity] || User;
                  return (
                    <TableRow key={log.id || idx} data-testid={`audit-row-${idx}`}>
                      <TableCell className="text-sm text-slate-500 align-top">
                        {formatTimestamp(log.timestamp)}
                      </TableCell>
                      <TableCell className="align-top">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center shrink-0">
                            <User className="w-3 h-3" />
                          </div>
                          <span className="text-sm">{log.user}</span>
                        </div>
                      </TableCell>
                      <TableCell className="align-top">
                        <Badge className={`${ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-700'} text-[11px]`}>
                          {log.action?.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="align-top">
                        <div className="flex items-center gap-1.5 text-sm">
                          <EntityIcon className="w-3.5 h-3.5 text-slate-400" />
                          <span className="capitalize">{log.entity}</span>
                        </div>
                      </TableCell>
                      <TableCell className="align-top">
                        <div className="text-sm text-slate-700">{log.details}</div>
                        {renderEntityDetails(log)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">
          Showing {logs.length} of {pagination.total} logs
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page === 1}
            onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm">
            Page {pagination.page} of {pagination.total_pages || 1}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page >= pagination.total_pages}
            onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Feedback Popup */}
      <Dialog open={!!feedbackPopup} onOpenChange={() => setFeedbackPopup(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Interview Feedback</DialogTitle>
          </DialogHeader>
          {feedbackPopup && (
            <div className="space-y-3" data-testid="audit-feedback-popup">
              {feedbackPopup.metadata?.candidate_name && (
                <div>
                  <p className="text-sm text-muted-foreground">Candidate</p>
                  <p className="font-medium">{feedbackPopup.metadata.candidate_name}</p>
                </div>
              )}
              {feedbackPopup.metadata?.round_name && (
                <div>
                  <p className="text-sm text-muted-foreground">Round</p>
                  <p>{feedbackPopup.metadata.round_name}</p>
                </div>
              )}
              {feedbackPopup.recommendation && (
                <div>
                  <p className="text-sm text-muted-foreground">Recommendation</p>
                  <Badge variant={feedbackPopup.recommendation?.includes('Hire') ? 'default' : 'destructive'}>
                    {feedbackPopup.recommendation}
                  </Badge>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground mb-1">Feedback</p>
                {feedbackPopup.loading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-lg p-3 text-sm whitespace-pre-wrap">
                    {feedbackPopup.feedback || 'No feedback provided.'}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogs;
